import { useSelector } from "@tanstack/react-store";
import { VaporPageStickable } from "@vapor/react-contrib-tax-compliance";
import { DateRange } from "@vapor/react-contrib-tax-compliance/DateRange/DateRange";
import { VaporToolbar } from "@vapor/react-custom";
import { Box, Button, CircularProgress, Stack } from "@vapor/react-material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { APP_VERSION, VITE_APP_ENV } from "../../../config";
import { useErrorNotification } from "../../../core/hooks/useErrorNotification";
import { useSuccessNotification } from "../../../core/hooks/useSuccessNotification";
import { useWarningNotification } from "../../../core/hooks/useWarningNotification";

import type { TaxSubmissionDTO } from "../../../core/api/use-get-tax-submissions/TaxSubmissionDTO";
import { useGetTaxSubmissions } from "../../../core/api/use-get-tax-submissions/useGetTaxSubmissions";
import { useSendTaxSubmission } from "../../../core/api/use-send-tax-submission/useSendTaxSubmission";
import { store } from "../../../store/store";
import { taxSubmissionsStoreSlice } from "../../../store/taxSubmissionSearchFiltersStoreSlice";
import { TaxSubmissionsTableHeader } from "./components/tax-submissions-table-header/TaxSubmissionsTableHeader";
import { TaxSubmissionsTable } from "./components/tax-submissions-table/TaxSubmissionsTable";
import type { DetailDrawerParams } from "./drawers/useDetailDrawer";
import { useDetailDrawer } from "./drawers/useDetailDrawer";
import type { F24PreviewDrawerParams } from "./drawers/useF24PreviewDrawer";
import { useF24PreviewDrawer } from "./drawers/useF24PreviewDrawer";
import type { OutcomePreviewDrawerParams } from "./drawers/useOutcomePreviewDrawer";
import { useOutcomePreviewDrawer } from "./drawers/useOutcomePreviewDrawer";
import type { SogeiOutcomeDrawerParams } from "./drawers/useSogeiOutcomeDrawer";
import { useSogeiOutcomeDrawer } from "./drawers/useSogeiOutcomeDrawer";
import { useUploadDrawer } from "./drawers/useUploadDrawer";

export const Home = () => {
    const { showTextMessage: showWarningMessage } = useWarningNotification();
    const { t } = useTranslation();
    const devOrTestEnv =
        VITE_APP_ENV === "development" || VITE_APP_ENV === "test";

    const expiringDays = 7;

    const filters = useSelector(
        store,
        (state) => state.taxSubmissionsSearchFilters,
    );

    const subscriptions = useSelector(
        store,
        (state) => state.session.subscriptions,
    );

    const { data: taxSubmissions } = useGetTaxSubmissions();

    const { showTextMessage: showErrorTextMessage } = useErrorNotification();
    const { showTextMessage } = useSuccessNotification();

    // states -> checkedNotSentTaxSubmissions
    const [checkedNotSentTaxSubmissions, setCheckedNotSentTaxSubmissions] =
        useState<TaxSubmissionDTO[]>([]);

    // states -> drawer

    // states -> header right width used to create a fake header left spacer in order to center title and subtitle
    const headerRightRef = useRef<HTMLDivElement | null>(null);
    const [headerRightWidth, setHeaderRightWidth] = useState(0);

    // states -> drawer
    const [previewOutcomeParams, setPreviewOutcomeParams] =
        useState<OutcomePreviewDrawerParams>();
    const [f24PreviewParams, setF24PreviewParams] =
        useState<F24PreviewDrawerParams>();
    const [previewSogeiOutcomeParams, setPreviewSogeiOutcomeParams] =
        useState<SogeiOutcomeDrawerParams>({ content: "", loading: false });
    const [selectedTaxSubmission, setSelectedTaxSubmission] =
        useState<DetailDrawerParams>();
    const [showUploadTaxSubmission, setShowUploadTaxSubmission] =
        useState(false);

    const {
        isPending: loadingSendTaxSubmission,
        mutateAsync: sendTaxSubmission,
    } = useSendTaxSubmission();

    const sogeiDrawer = useSogeiOutcomeDrawer(
        previewSogeiOutcomeParams,
        setPreviewSogeiOutcomeParams,
    );
    const outcomeDrawer = useOutcomePreviewDrawer(
        previewOutcomeParams,
        setPreviewOutcomeParams,
        setSelectedTaxSubmission,
    );
    const f24Drawer = useF24PreviewDrawer(
        f24PreviewParams,
        setF24PreviewParams,
        setSelectedTaxSubmission,
    );
    const detailDrawer = useDetailDrawer(
        selectedTaxSubmission,
        setSelectedTaxSubmission,
        setF24PreviewParams,
        setPreviewOutcomeParams,
    );
    const uploadDrawer = useUploadDrawer(
        showUploadTaxSubmission,
        setShowUploadTaxSubmission,
    );

    // Sub-drawers (sogei/outcome/f24) take precedence over the detail that
    // opened them: the sub-drawer's back arrow only closes itself, revealing
    // the underlying detail.
    const drawerContent =
        sogeiDrawer ??
        outcomeDrawer ??
        f24Drawer ??
        detailDrawer ??
        uploadDrawer;

    useEffect(() => {
        const updateWidth = () => {
            if (headerRightRef.current) {
                setHeaderRightWidth(headerRightRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener("resize", updateWidth);

        return () => {
            window.removeEventListener("resize", updateWidth);
        };
    }, []);

    // functions
    const onClickSendTaxSubmissions = async () => {
        const { succeded, failed } = await sendTaxSubmission(
            checkedNotSentTaxSubmissions.map((taxSubmission) => {
                return {
                    id: taxSubmission.id,
                    uploadFlow: taxSubmission.governmentAgency.type,
                };
            }),
        );

        succeded.forEach((taxSubmission) => {
            const name =
                taxSubmissions.data.find((data) => data.id === taxSubmission.id)
                    ?.fileName ?? taxSubmission.id;

            showTextMessage(
                t("views.home.notifications.send_taxSubmission_succeded", {
                    name: name,
                }),
            );
        });

        failed.forEach((taxSubmission) => {
            const name =
                taxSubmissions.data.find((data) => data.id === taxSubmission.id)
                    ?.fileName ?? taxSubmission.id;

            showErrorTextMessage(
                t("views.home.notifications.send_taxSubmission_failed", {
                    name: name,
                    error: taxSubmission.error,
                }),
            );
        });

        setCheckedNotSentTaxSubmissions([]);
    };

    const handleDateRangeValidation = () => {
        showWarningMessage(t("views.home.date_range.warning"));
    };

    const resolveSubtitle = () => {
        let subtitle: string = t("views.home.header.subtitle");
        if (devOrTestEnv) {
            subtitle += `${"\n"}${VITE_APP_ENV} ${APP_VERSION}`;
        }
        return subtitle;
    };

    return (
        <VaporPageStickable
            drawer={drawerContent}
            headerLeft={
                // fake spacer to center title and subtitle
                <Box visibility="hidden" width={headerRightWidth}></Box>
            }
            headerBottom={
                <Stack
                    direction="row"
                    spacing={10}
                    padding={2}
                    justifyContent="space-between"
                    alignContent="baseline"
                >
                    <Box flexGrow={2}>
                        <TaxSubmissionsTableHeader
                            uploadDisabled={
                                loadingSendTaxSubmission ||
                                subscriptions.every((sub) => sub.isReadOnly)
                            }
                            onClickUploadTaxSubmission={
                                setShowUploadTaxSubmission
                            }
                        />
                    </Box>
                </Stack>
            }
            headerBottomDivider
            headerRight={
                <Stack
                    alignItems="center"
                    direction="row"
                    id="header-right"
                    spacing={2}
                    mr={2}
                    ref={headerRightRef}
                >
                    <DateRange
                        disableValidationAlert
                        endDate={filters.deadlineTo}
                        handleCustomValidation={handleDateRangeValidation}
                        isValidDateRange={false}
                        onChangeDates={(startDate, endDate) => {
                            taxSubmissionsStoreSlice.updateValues({
                                deadlineFrom: startDate,
                                deadlineTo: endDate,
                            });
                        }}
                        showPeriodSelectionMenu
                        startDate={filters.deadlineFrom}
                        expiringDays={expiringDays}
                    />
                </Stack>
            }
            stickyHeader
            sxContent={{ padding: 2 }}
            title={t("views.home.header.title")}
            subtitle={resolveSubtitle()}
            sxHeader={{ whiteSpace: "pre-line" }}
        >
            <Stack spacing={2}>
                <TaxSubmissionsTable
                    onCheckNotSentTaxSubmissions={(
                        taxSubmissions: TaxSubmissionDTO[],
                    ) => {
                        setCheckedNotSentTaxSubmissions(taxSubmissions);
                    }}
                    onClickTaxSubmission={(taxSubmissionId) =>
                        setSelectedTaxSubmission({
                            id: taxSubmissionId,
                            revoke: false,
                        })
                    }
                    onClickPreviewSogeiOutcome={(content, loading) => {
                        setPreviewSogeiOutcomeParams({
                            content: content,
                            loading: loading,
                        });
                    }}
                    onClickRevokeTaxSubmission={(taxSubmissionId) =>
                        setSelectedTaxSubmission({
                            id: taxSubmissionId,
                            revoke: true,
                        })
                    }
                />
                {checkedNotSentTaxSubmissions.length > 0 && (
                    <Box sx={{ "& div": { left: 0 } }}>
                        <VaporToolbar
                            contentRight={[
                                <Button
                                    disabled={loadingSendTaxSubmission}
                                    onClick={onClickSendTaxSubmissions}
                                    variant="contained"
                                >
                                    {t("views.home.actions.send_taxSubmission")}
                                    {loadingSendTaxSubmission && (
                                        <CircularProgress
                                            color="inherit"
                                            sx={{ ml: 1 }}
                                            size={16}
                                        />
                                    )}
                                </Button>,
                            ]}
                            size="medium"
                            variant="regular"
                        />
                    </Box>
                )}
            </Stack>
        </VaporPageStickable>
    );
};
